import { useCallback, useEffect, useRef, useState, type CSSProperties } from "react";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import LinearProgress from "@mui/material/LinearProgress";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import DeleteIcon from "@mui/icons-material/Delete";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import WifiOffIcon from "@mui/icons-material/WifiOff";
import { useConfirm } from "material-ui-confirm";
import {
  ArticleError,
  articleFromText,
  fetchArticle,
  type ExtractedArticle,
} from "../lib/article";
import { BUILD_ID } from "../lib/build";
import { OcrError, recognizeImage } from "../lib/ocr";
import { isNative, scanNativeDocument } from "../lib/platform";
import {
  deleteErasure,
  listErasures,
  loadErasure,
  saveErasure,
  type ErasureSummary,
} from "../lib/db";
import {
  ENTER_SLOTS,
  ERASED,
  EXIT_SLOTS,
  LIBRARY_STAGGER_LIMIT,
  NAME,
  TITLE,
  closeDelay,
  enterDelay,
  eraseDelay,
  exitDelay,
  exitDuration,
  survives,
  survivorSlot,
  wait,
} from "../lib/motion";
import { generateKey, looksLikeUrl } from "../lib/text";
import { defaultStyle } from "../state/styles";
import type { ErasureDocument } from "../state/reducer";

interface SplashProps {
  isOnline: boolean;
  onOpen: (document: ErasureDocument) => void;
  sharedInput?: string | null;
  onSharedInputConsumed?: () => void;
}

/** The reader-facing message for a failure, or a generic one for a surprise. */
const messageFor = (caught: unknown): string =>
  caught instanceof ArticleError || caught instanceof OcrError
    ? caught.message
    : "Something went wrong. Please try again.";

const hostnameOf = (url: string | undefined): string => {
  if (!url) return "";
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return "";
  }
};

/** The library screen: start a new erasure, or reopen a saved one. */
const Splash = ({
  isOnline,
  onOpen,
  sharedInput = null,
  onSharedInputConsumed,
}: SplashProps) => {
  const [input, setInput] = useState("");
  const [saved, setSaved] = useState<ErasureSummary[]>([]);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [isBusy, setIsBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** Non-null only while an image is being read; a fraction from 0 to 1. */
  const [scanProgress, setScanProgress] = useState<number | null>(null);
  /** True once the splash has started erasing itself away for the editor. */
  const [isLeaving, setIsLeaving] = useState(false);
  /**
   * True once a farewell has played at all. A reader who is back here after one
   * is looking at an error message, not at the opening titles, so the entrance
   * replays without its stagger.
   */
  const [hasLeft, setHasLeft] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);
  const importedShare = useRef<string | null>(null);
  const confirm = useConfirm();

  useEffect(() => {
    let cancelled = false;
    listErasures()
      .then((entries) => {
        if (cancelled) return;
        setSaved(entries);
        setHasLoaded(true);
      })
      .catch(() => {
        if (cancelled) return;
        setError("Couldn't open your saved erasures.");
        setHasLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  /**
   * Starts erasing the splash away, and resolves when the last letter of the
   * title has gone back under its bar. Callers await this before handing the
   * document over, so the editor arrives exactly as the farewell ends rather
   * than at a guessed time on either side of it.
   */
  const beginFarewell = useCallback((): Promise<void> => {
    setIsLeaving(true);
    setHasLeft(true);
    return wait(exitDuration());
  }, []);

  /** Puts the splash back after a farewell that came to nothing. */
  const abandonFarewell = useCallback(() => setIsLeaving(false), []);

  const open = useCallback(
    async (key: string) => {
      const document = await loadErasure(key);
      if (!document) {
        setError("That erasure could not be found.");
        return;
      }
      await beginFarewell();
      onOpen(document);
    },
    [beginFarewell, onOpen],
  );

  /** Stores a freshly extracted article, ready to be handed to the editor. */
  const storeArticle = useCallback(async (article: ExtractedArticle) => {
    const key = generateKey();
    const document: ErasureDocument = {
      key,
      meta: { ...article.meta, name: key },
      page: article.page,
      style: defaultStyle,
      styleKey: "defaultStyle",
    };
    await saveErasure(document);
    return document;
  }, []);

  /**
   * Turns whatever the reader typed into a new erasure. A URL is fetched through
   * the proxy; anything else is treated as text to erase directly.
   *
   * The farewell starts before the fetch rather than after it, so the two run
   * together: the title erasing itself is what the reader waits on, and a fetch
   * that beats it costs nothing.
   */
  const createFromValue = useCallback(
    async (untrimmedValue: string) => {
      const value = untrimmedValue.trim();
      if (value.length === 0) return;

      setIsBusy(true);
      setError(null);
      const farewell = beginFarewell();
      try {
        const document = await storeArticle(
          looksLikeUrl(value) ? await fetchArticle(value) : articleFromText(value),
        );
        await farewell;
        onOpen(document);
      } catch (caught) {
        setError(messageFor(caught));
        setIsBusy(false);
        abandonFarewell();
      }
    },
    [abandonFarewell, beginFarewell, onOpen, storeArticle],
  );

  const create = useCallback(() => createFromValue(input), [createFromValue, input]);

  useEffect(() => {
    if (!sharedInput || isBusy || importedShare.current === sharedInput) return;
    importedShare.current = sharedInput;
    queueMicrotask(() => {
      onSharedInputConsumed?.();
      void createFromValue(sharedInput);
    });
  }, [createFromValue, isBusy, onSharedInputConsumed, sharedInput]);

  /**
   * Reads a photographed or scanned page and erases that. Recognition runs in
   * this browser, so the image is never uploaded anywhere.
   *
   * Unlike `create`, the farewell waits until recognition is done: it takes long
   * enough to need a progress bar, and the reader has to be able to read it.
   */
  const scan = useCallback(
    async (file: File) => {
      setIsBusy(true);
      setError(null);
      setScanProgress(0);
      try {
        const text = await recognizeImage(file, { onProgress: setScanProgress });
        const document = await storeArticle(articleFromText(text));
        await beginFarewell();
        onOpen(document);
      } catch (caught) {
        setError(messageFor(caught));
        setIsBusy(false);
        setScanProgress(null);
        abandonFarewell();
      }
    },
    [abandonFarewell, beginFarewell, onOpen, storeArticle],
  );

  const scanDocument = useCallback(async () => {
    setIsBusy(true);
    setError(null);
    setScanProgress(0);
    try {
      const text = await scanNativeDocument();
      const document = await storeArticle(articleFromText(text));
      await beginFarewell();
      onOpen(document);
    } catch (caught) {
      setError(messageFor(caught));
      setIsBusy(false);
      setScanProgress(null);
      abandonFarewell();
    }
  }, [abandonFarewell, beginFarewell, onOpen, storeArticle]);

  const remove = useCallback(
    async (key: string) => {
      try {
        await confirm({ description: "Are you sure you want to delete this erasure?" });
      } catch {
        return; // Reader cancelled.
      }
      await deleteErasure(key);
      setSaved((entries) => entries.filter((entry) => entry.key !== key));
    },
    [confirm],
  );

  /**
   * Every block of the interface is a slot on the shared timeline: one number
   * for its place in the sweep down the screen, one for its place in the sweep
   * back off it. Nothing here picks its own timing.
   */
  const blockClass = isLeaving ? "clear-out" : "rise-in";
  // The library's entries animate on the way in only; their container does the
  // leaving for them, so they must not restart their entrance underneath it.
  const entryClass = isLeaving ? undefined : "rise-in";
  const delayFor = (enter: number, exit: number): CSSProperties =>
    ({
      "--delay": isLeaving ? exitDelay(exit) : hasLeft ? "0ms" : enterDelay(enter),
    }) as CSSProperties;

  return (
    <>
      {!isOnline && (
        <Alert
          severity="warning"
          icon={<WifiOffIcon />}
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            zIndex: 9999,
            borderRadius: 0,
          }}
        >
          {isNative()
            ? "You're offline. You can still scan pages, open saved erasures and paste text."
            : "You're offline. You can still open saved erasures and paste text."}
        </Alert>
      )}

      <div
        className="content-container"
        style={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-end",
          height: "40vh",
          alignItems: "center",
          margin: "0 5%",
        }}
      >
        {/*
          The whole word is printed under a row of redaction bars that retract to
          reveal it; then the middle of it is rubbed out and the survivors close
          up into the name. Labelled with the name rather than the word, and the
          letters hidden, so it is one heading to a screen reader rather than
          eleven — and the heading is what the reader ends up looking at, not the
          word it was cut from.
        */}
        <Typography
          variant="h1"
          aria-label={NAME}
          sx={{ fontSize: "clamp(3rem, 18vw, 8rem)" }}
        >
          {TITLE.split("").map((letter, index) => {
            /*
              A letter of the run is either being rubbed out or already gone.
              Already gone covers both the reader who is back after a failed
              fetch — who has watched this once and should not sit through it
              again — and the farewell, which redacts the name rather than
              re-running the erasure underneath it.
            */
            if (!survives(index)) {
              const slot = ERASED.indexOf(index as never);
              return (
                <span
                  key={index}
                  aria-hidden="true"
                  className={`title-letter title-letter--${
                    isLeaving || hasLeft ? "gone" : "erasing"
                  }`}
                  style={
                    {
                      // The bar over this letter lifts on the entrance sweep
                      // with all the others; only the rub waits for the word to
                      // be standing.
                      ...delayFor(index, survivorSlot(index)),
                      "--rub-delay": eraseDelay(slot),
                      "--close-delay": closeDelay(slot),
                    } as CSSProperties
                  }
                >
                  <span className="title-letter__ink">{letter}</span>
                </span>
              );
            }

            return (
              <span
                key={index}
                aria-hidden="true"
                className={`title-letter${isLeaving ? " title-letter--leaving" : ""}`}
                style={delayFor(index, survivorSlot(index))}
              >
                <span className="title-letter__ink">{letter}</span>
              </span>
            );
          })}
        </Typography>

        <form
          onSubmit={(event) => {
            event.preventDefault();
            void create();
          }}
          className={blockClass}
          style={{
            ...delayFor(ENTER_SLOTS.form, EXIT_SLOTS.form),
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            width: "100%",
            marginTop: "10px",
          }}
        >
          <div
            style={{
              display: "flex",
              gap: "10px",
              width: "100%",
              maxWidth: "480px",
              alignItems: "flex-start",
            }}
          >
            <TextField
              size="small"
              variant="outlined"
              // Multiline so pasted prose keeps its paragraph breaks: a
              // single-line input silently collapses "a\n\nb" to "ab", which
              // would erase the paragraph structure of anything pasted in.
              multiline
              maxRows={8}
              value={input}
              onChange={(event) => setInput(event.target.value)}
              onKeyDown={(event) => {
                // Enter alone inserts a newline in a multiline field, so submit
                // on a modified Enter, as chat inputs do.
                if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) {
                  event.preventDefault();
                  void create();
                }
              }}
              placeholder={isOnline ? "paste a URL or text..." : "paste text..."}
              label="Article URL or text"
              InputLabelProps={{ shrink: true }}
              sx={{ flex: 1 }}
              disabled={isBusy}
            />
            <Button
              type="submit"
              variant="contained"
              disabled={isBusy || input.trim().length === 0}
              style={{ backgroundColor: isBusy ? undefined : "black", color: "white" }}
            >
              {isBusy ? <CircularProgress size={20} sx={{ color: "white" }} /> : "Create"}
            </Button>
          </div>

          <div
            className={blockClass}
            style={{
              ...delayFor(ENTER_SLOTS.scan, EXIT_SLOTS.scan),
              width: "100%",
              maxWidth: "480px",
              marginTop: "4px",
            }}
          >
            <input
              ref={fileInput}
              type="file"
              accept="image/*"
              aria-label="Photo of a page to scan"
              // Off-screen rather than hidden: a file input inside a `display:
              // none` wrapper is unreachable by some assistive technology, and
              // the button below is what the reader actually operates.
              style={{
                position: "absolute",
                width: "1px",
                height: "1px",
                opacity: 0,
                pointerEvents: "none",
              }}
              onChange={(event) => {
                const file = event.target.files?.[0];
                // Cleared so that choosing the same photo twice in a row still
                // fires a change event.
                event.target.value = "";
                if (file) void scan(file);
              }}
            />
            {/*
              A real button rather than a `<label>`, so the keyboard reaches the
              file picker the same way the pointer does.
            */}
            <Button
              variant="text"
              size="small"
              startIcon={<PhotoCameraIcon />}
              disabled={isBusy}
              onClick={() => {
                if (isNative()) void scanDocument();
                else fileInput.current?.click();
              }}
              sx={{ color: "#555", textTransform: "none" }}
            >
              {scanProgress === null
                ? "or scan a photo of a page"
                : isNative()
                  ? "reading the page…"
                  : `reading the page… ${Math.round(scanProgress * 100)}%`}
            </Button>
            {scanProgress !== null && !isNative() && (
              <LinearProgress
                variant="determinate"
                value={scanProgress * 100}
                sx={{ marginTop: "6px" }}
              />
            )}
          </div>

          {error && (
            <Alert
              severity="error"
              sx={{ marginTop: "10px", width: "100%", maxWidth: "480px" }}
            >
              {error}
            </Alert>
          )}
        </form>
      </div>

      {/*
        The library's entries arrive one by one, so the container itself only
        animates on the way out — otherwise a fading parent and fading children
        would be two fades stacked on one another.
      */}
      <div
        className={`content-container${isLeaving ? " clear-out" : ""}`}
        style={{
          ...delayFor(ENTER_SLOTS.library, EXIT_SLOTS.library),
          height: "45vh",
          overflowY: "auto",
          position: "relative",
          margin: "0 5%",
        }}
      >
        {!hasLoaded && (
          <div
            className={entryClass}
            style={{
              ...delayFor(ENTER_SLOTS.library, EXIT_SLOTS.library),
              display: "flex",
              justifyContent: "center",
              paddingTop: "20px",
            }}
          >
            <CircularProgress style={{ color: "black" }} />
          </div>
        )}

        {hasLoaded && saved.length === 0 && (
          <Typography
            className={entryClass}
            style={delayFor(ENTER_SLOTS.library, EXIT_SLOTS.library)}
            sx={{ textAlign: "center", color: "#666", marginTop: "20px" }}
          >
            Paste a link or some text above to start your first erasure.
          </Typography>
        )}

        {hasLoaded && saved.length > 0 && (
          <List style={{ padding: "10px" }}>
            {saved.map(({ key, title, url }, index) => (
              <ListItem
                key={key}
                className={entryClass}
                // The stagger stops after a handful of entries, so a long
                // library does not take several seconds to finish arriving.
                style={delayFor(
                  ENTER_SLOTS.library + Math.min(index, LIBRARY_STAGGER_LIMIT),
                  EXIT_SLOTS.library,
                )}
                disablePadding
                secondaryAction={
                  <IconButton
                    edge="end"
                    aria-label={`Delete ${title || key}`}
                    onClick={() => void remove(key)}
                  >
                    <DeleteIcon />
                  </IconButton>
                }
              >
                <ListItemButton onClick={() => void open(key)}>
                  <ListItemText
                    primary={title || key.split("-").join(" ")}
                    secondary={hostnameOf(url)}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        )}
      </div>

      <div
        className={`footer-container ${blockClass}`}
        style={{
          ...delayFor(ENTER_SLOTS.footer, EXIT_SLOTS.footer),
          position: "fixed",
          bottom: "env(safe-area-inset-bottom)",
          padding: "15px env(safe-area-inset-right) 15px env(safe-area-inset-left)",
          // Centred by its own insets rather than by a transform: the entrance
          // animates `transform`, and would otherwise leave the footer parked
          // half a screen to the right of where it belongs.
          left: "5%",
          right: "5%",
          textAlign: "center",
          backgroundColor: "grey",
          color: "white",
          borderRadius: "5px",
          marginBottom: "20px",
        }}
      >
        <a
          href="https://www.buttondown.email/justinwolfe"
          style={{ color: "white" }}
          target="_blank"
          rel="noopener noreferrer"
        >
          subscribe to erasure club
        </a>
        <br />
        for weekly challenges
        <div
          style={{ marginTop: "6px", fontSize: "10px", opacity: 0.7 }}
          data-testid="build-id"
        >
          {BUILD_ID}
        </div>
      </div>
    </>
  );
};

export default Splash;
